import React, { useCallback, useMemo, useState } from 'react';
import { IconButton } from '@mui/material';
import { toast } from 'react-toastify';

import { PLACEMENT } from '@components/CustomTextInput';
import ResourcesList from '@shared/Resources/shared/components/ResourcesList';
import {
  bulkCreateSpeechTopics,
  deleteSpeechTopic,
  fetchSpeechTopics,
  updateSpeechTopic,
} from '@services/speechTopicsService';
import { exportSpeechTopicsCSV, parseSpeechTopicsCSV } from '../speechTopicsCSV';
import {
  StyledCustomTextInput as HeaderStyledCustomTextInput,
  StyledSearchIcon as HeaderStyledSearchIcon,
} from '@containers/Admin/containers/ResourceManager/components/ResourceHeader/styles';
import { createSpeechTopicsTableConf } from '../config';
import { SpeechTopicsRoot } from '../styles';
import {
  ExportIcon,
  HiddenFileInput,
  ImportIcon,
  ListArea,
  Toolbar,
} from '@shared/Resources/Stimulus/shared/tabStyles';
import Text, { TEXT_COLOR, TEXT_SIZE } from '@components/Text';
import { ContentRow, DetailsBody, DetailsPane } from '@shared/Resources/Stimulus/shared/detailsPaneStyles';
import { useCsvResourceTab } from '@shared/Resources/Stimulus/shared/useCsvResourceTab';
import { TOAST_OPTIONS, toToastMessage } from '@shared/Resources/Stimulus/shared/toastUtils';
import StimulusSpeechTopicDetailsEditor from '@shared/Resources/Stimulus/shared/StimulusSpeechTopicDetailsEditor';
import { applyOptimisticSave } from '@shared/Resources/Stimulus/shared/optimisticSave';
import DeleteConfirmDialog from '@shared/Resources/Stimulus/shared/DeleteConfirmDialog';

const SpeechTopicsList = () => {
  const [saveLoading, setSaveLoading] = useState(false);

  const {
    filteredItems: filteredTopics,
    search,
    setSearch,
    loading,
    selectedItem: selectedTopic,
    setSelectedItem,
    setItems,
    fileInputRef,
    handleClickImport,
    handleImportCSV,
    handleExportCSV,
    handleDelete,
    handleConfirmDelete,
    handleCancelDelete,
    pendingDeleteId,
    handleSelect,
  } = useCsvResourceTab({
    fetchItems: fetchSpeechTopics,
    parseCsvFile: parseSpeechTopicsCSV,
    bulkCreate: bulkCreateSpeechTopics,
    exportCsv: exportSpeechTopicsCSV,
    deleteItem: deleteSpeechTopic,
    entityLabel: 'topics',
    enableSelection: true,
    messages: {
      loadFailed: 'Failed to load topics',
      imported: (count) => `Imported ${count} topics`,
      exported: (count) => `Exported ${count} topics`,
      deleted: 'Deleted',
    },
  });

  const tableConf = useMemo(() => createSpeechTopicsTableConf({ onDelete: handleDelete }), [handleDelete]);

  const handleSaveSpeechTopic = useCallback(
    (input) => applyOptimisticSave({
      selectedItem: selectedTopic,
      input,
      setLoading: setSaveLoading,
      setItems,
      setSelectedItem,
      updateItem: updateSpeechTopic,
      successMessage: 'Speech topic',
      errorMessage: 'Failed to update speech topic',
      onSuccess: (label) => toast.success(`${label} updated`, TOAST_OPTIONS),
      onError: (err, fallback) => toast.error(toToastMessage(err, fallback), TOAST_OPTIONS),
    }),
    [selectedTopic, setItems, setSelectedItem]
  );

  const renderToolbar = () => (
    <Toolbar>
      <HeaderStyledCustomTextInput
        placeholder="Search by word in text..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        Icon={() => <HeaderStyledSearchIcon />}
        iconPlacement={PLACEMENT.RIGHT}
        autofocus
        name="speech-topics-search"
        id="speech-topics-search"
      />
      <IconButton
        aria-label="Import from CSV"
        title="Import from CSV"
        onClick={handleClickImport}
        size="small"
      >
        <ImportIcon />
      </IconButton>
      <IconButton aria-label="Download CSV" title="Download CSV" onClick={handleExportCSV} size="small">
        <ExportIcon />
      </IconButton>
      <HiddenFileInput type="file" accept=".csv" ref={fileInputRef} onChange={handleImportCSV} />
    </Toolbar>
  );

  if (loading) return <div>Loading...</div>;

  return (
    <>
      <SpeechTopicsRoot>
        {renderToolbar()}
        <ContentRow>
          <ListArea>
            <ResourcesList
              items={filteredTopics}
              tableConf={tableConf}
              selectedItem={selectedTopic}
              onSelect={handleSelect}
              showSelectionArrow={false}
            />
          </ListArea>
          <DetailsPane>
            <DetailsBody>
              {!selectedTopic ? (
                <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DISABLED}>
                  Select a speech topic to view details
                </Text>
              ) : (
                <StimulusSpeechTopicDetailsEditor
                  item={selectedTopic}
                  onSave={handleSaveSpeechTopic}
                  saving={saveLoading}
                />
              )}
            </DetailsBody>
          </DetailsPane>
        </ContentRow>
      </SpeechTopicsRoot>
      {pendingDeleteId && (
        <DeleteConfirmDialog
          message="Are you sure you want to delete this speech topic?"
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}
    </>
  );
};

export default SpeechTopicsList;
